import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { VideoView, useVideoPlayer } from 'expo-video';
import { isNumber } from 'lodash-es';
import LottieView from 'lottie-react-native';
import { LayoutChangeEvent, Pressable, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { TakeoverVolumeButton } from 'components/app-takeover/takeover-mute-button';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import Picture from 'components/picture';
import { onboardingIsLikelyCompleteDeferred } from 'state/onboarding/hooks/use-onboarding-content';
import { primitive } from 'styles/constants/primitives';
import { Deferred } from 'utils/Deferred';
import { isCA, isWeb } from 'utils/environment';
import { animatedSplashScreenHiddenDeferred } from 'utils/hide-splash-screen';
import { TraceName } from 'utils/performance';
import { stopTrace } from 'utils/performance/trace';

import { CTAButtons } from '../components/cta-button';
import { ISimpleHomePageProps } from '../types';
import { sendHeroClickEvent } from '../utils';

const rgbColorMidpoint = Number('0x777777');

// this only needs to happen once per app load
const onImageLoadedDeferred = new Deferred();
if (__DEV__) {
  onImageLoadedDeferred.resolve(); // live reload throws this off in dev
}

const _SimpleHomePageMobile = ({
  numberOfItemsInCart,
  serviceMode,
  item,
  flatListDimensions,
}: ISimpleHomePageProps) => {
  const [safeAreaHeight, setSafeAreaHeight] = useState<Number | null>(null);
  const appTakeover = useAppTakeover();
  const [lottieFinished, setLottieFinished] = useState(false);
  const { targetSimpleHomePageIds, containerBackgroundColor } = appTakeover?.homePage || {};
  const isTakeoverActive = targetSimpleHomePageIds?.includes(item._id);
  const router = useRouter();
  const animationCanPlayRef = useRef(false);

  // the following is due to a LottieView type mismatch between native and web
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const animationRef = useRef<any>(null);

  const {
    mobileImage,
    mobileImageLottie,
    mobileVideo,
    altImageText,
    _id,
    imageLink,
    mobileImageBackgroundColor,
    imageDisclaimerText,
    daypart,
  } = item;

  const imageAlt = altImageText?.locale ?? '';
  const image = mobileImage?.locale;
  const lottieUrl = mobileImageLottie?.locale?.asset?.url;
  const videoUri = mobileVideo?.locale?.asset?.url;

  const onImageLoaded = useCallback(() => {
    onImageLoadedDeferred.resolve();
  }, []);

  useEffect(() => {
    // web doesn't have these things...
    (isWeb
      ? Promise.resolve()
      : Promise.all([
          onImageLoadedDeferred.promise, // waiting until the image is loaded until playing is required on web or it doesn't work
          animatedSplashScreenHiddenDeferred.promise, // don't play if splash screen is up
          onboardingIsLikelyCompleteDeferred.promise, // don't play if onboarding modal is up
        ])
    ).then(() => {
      stopTrace(TraceName.JAVASCRIPT_START_TO_SIMPLE_HOME_PAGE_LOADED, { shpId: _id });
      stopTrace(TraceName.HOME_LOAD);
      animationCanPlayRef.current = true;
      animationRef?.current?.play();
    });
  }, []);

  // reverse the disclaimer color to white-ish if the background color is dark (below 50% of RGB space)
  const reverseDisclaimerColor =
    mobileImageBackgroundColor &&
    imageDisclaimerText?.locale &&
    Number('0x' + mobileImageBackgroundColor.replace('#', '')) < rgbColorMidpoint;

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;
      // this sets a max height if the safe area of the image is going to get cut off horizontally
      // (this is extremely rare only with devices that are very tall and narrow)
      if (
        width &&
        isNumber(width) &&
        height &&
        isNumber(height) &&
        safeAreaHeight === null &&
        width / height < 0.625
      ) {
        // the aspect ratio of the image safe area is 1.5x the width
        setSafeAreaHeight(width * 1.5);
      }
    },
    [safeAreaHeight]
  );

  const handleOnPress = () => {
    if (imageLink) {
      sendHeroClickEvent({ imageLink, daypart });
      router.navigate(imageLink);
    }
  };

  const safeAreaStyle = safeAreaHeight ? { maxHeight: safeAreaHeight as any } : undefined;

  return (
    <View
      testID="simple-home-page"
      style={{
        height: flatListDimensions?.height,
        ...(isTakeoverActive ? { backgroundColor: containerBackgroundColor } : {}),
      }}
    >
      <Pressable
        style={[styles.pressable, { backgroundColor: mobileImageBackgroundColor || undefined }]}
        onPress={handleOnPress}
        {...(imageLink ? { accessibilityLabel: imageAlt, role: 'button' } : {})}
      >
        {image && !lottieUrl && !videoUri && (
          <Picture
            priority="high"
            image={image}
            alt={imageAlt}
            onDisplay={onImageLoaded}
            style={[styles.image, safeAreaStyle]}
            onLayout={onLayout}
          />
        )}
        {lottieUrl && !videoUri && (
          <LottieView
            ref={animationRef}
            resizeMode="cover"
            hardwareAccelerationAndroid
            // handles breakfast->lunch transition if a rerender externally happens so animation plays
            autoPlay={isWeb || animationCanPlayRef.current}
            loop={false}
            style={[styles.lottie, safeAreaStyle]}
            // the mandatory spec of the lottie is 1536px. on web only hardcoding it to this scales correctly.
            webStyle={{ width: 1536 }}
            onAnimationLoaded={onImageLoaded}
            onAnimationFinish={() => {
              setLottieFinished(true);
            }}
            onLayout={onLayout}
            source={{ uri: lottieUrl }}
          />
        )}
        {videoUri && (
          <Video
            videoUri={videoUri}
            onLayout={onLayout}
            onLoaded={onImageLoaded}
            safeAreaStyle={safeAreaStyle}
          />
        )}
        {imageDisclaimerText?.locale && (
          <DisclaimerText
            style={reverseDisclaimerColor ? { color: primitive.bk.$mayoEggWhite } : undefined}
          >
            {imageDisclaimerText?.locale}
          </DisclaimerText>
        )}
        {isTakeoverActive && !isCA && <TakeoverVolumeButton lottieFinished={lottieFinished} />}
      </Pressable>
      <FooterContainer>
        <CTAButtons
          numberOfItemsInCart={numberOfItemsInCart}
          serviceMode={serviceMode}
          itemId={_id}
        />
      </FooterContainer>
    </View>
  );
};

const Video = ({
  videoUri,
  onLayout,
  onLoaded,
  safeAreaStyle,
}: {
  videoUri: string;
  onLayout: (event: LayoutChangeEvent) => void;
  onLoaded: () => void;
  safeAreaStyle: StyleProp<ViewStyle>;
}) => {
  const player = useVideoPlayer({ uri: videoUri }, player => {
    player.loop = true;
    player.muted = true;
    if (isWeb) {
      // web requires a timeout due to a bug, maybe could remove in SDK 54 https://github.com/expo/expo/issues/36350
      setTimeout(() => player.play(), 0);
    } else {
      player.play();
    }
  });
  return (
    <VideoView
      style={[styles.video, safeAreaStyle]}
      onLayout={onLayout}
      onFirstFrameRender={onLoaded}
      contentFit="fill"
      nativeControls={false}
      allowsFullscreen={false}
      allowsPictureInPicture={false}
      // @ts-ignore - VideoPlayer type mismatch between native and web
      player={player}
      playsInline
    />
  );
};

const styles = StyleSheet.create({
  pressable: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    flex: 1,
  },
  lottie: {
    height: '100%',
    width: '100%',
  },
  video: {
    flex: 1,
    aspectRatio: 1.5,
  },
});

const DisclaimerText = Text.withConfig({
  fontSize: '9px',
  marginBottom: '$2',
  marginLeft: '$5',
  alignSelf: 'flex-start',
});

const FooterContainer = Box.withConfig({
  paddingX: '$4',
  paddingY: '$4',
});

export const SimpleHomePageMobile = React.memo(_SimpleHomePageMobile);
